<template>
	<div class="donut-chart-container">
		<v-chart
			v-if="chartOption"
			ref="chart"
			:option="chartOption"
			:style="{ height: '280px', width: '100%' }"
			autoresize
			@click="handleClick"
		/>
		<div v-else class="no-data">No data available</div>
	</div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import VChart from 'vue-echarts';

use([CanvasRenderer, PieChart, TooltipComponent, LegendComponent]);

export default {
	name: 'SpendingDonutChart',
	components: { VChart },
	props: {
		categoryTotals: { type: Array, required: true },
		formatter: { type: Object, required: true },
		colourMode: { type: String, default: 'light' }
	},
	emits: ['category-click'],
	computed: {
		totalSpend() {
			return this.categoryTotals
				.filter(c => c.id !== 0) // Exclude income
				.reduce((sum, c) => sum + c.value, 0);
		},
		chartOption() {
			if (!this.categoryTotals || this.categoryTotals.length === 0) return null;

			const spendCategories = this.categoryTotals.filter(c => c.id !== 0 && c.value > 0);
			if (spendCategories.length === 0) return null;

			const textColor = this.colourMode === 'dark' ? '#e0e0e0' : '#333';

			return {
				tooltip: {
					trigger: 'item',
					formatter: (params) => {
						return `${params.name}: ${this.formatter.format(params.value)} (${params.percent}%)`;
					}
				},
				legend: {
					orient: 'vertical',
					right: 10,
					top: 'center',
					textStyle: { color: textColor }
				},
				series: [
					{
						name: 'Spending',
						type: 'pie',
						radius: ['50%', '75%'],
						center: ['35%', '50%'],
						avoidLabelOverlap: false,
						itemStyle: {
							borderRadius: 4,
							borderColor: this.colourMode === 'dark' ? '#1e1e1e' : '#fff',
							borderWidth: 2
						},
						label: {
							show: true,
							position: 'center',
							formatter: () => {
								return `{total|${this.formatter.format(this.totalSpend)}}\n{label|Total Spend}`;
							},
							rich: {
								total: {
									fontSize: 22,
									color: textColor,
									lineHeight: 30
								},
								label: {
									fontSize: 12,
									color: this.colourMode === 'dark' ? '#999' : '#666'
								}
							}
						},
						emphasis: {
							label: { show: true }
						},
						labelLine: { show: false },
						data: spendCategories.map(cat => ({
							value: cat.value,
							name: cat.name,
							itemStyle: { color: cat.colour },
							categoryId: cat.id
						}))
					}
				]
			};
		}
	},
	methods: {
		handleClick(params) {
			if (params.data?.categoryId !== undefined) {
				this.$emit('category-click', params.data.categoryId);
			}
		}
	}
};
</script>

<style scoped>
.donut-chart-container {
	width: 100%;
}

.no-data {
	height: 280px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--light-text-secondary);
}
</style>
